import type { ChatColleague, ChatConversation } from "@/lib/chatApi";

export type ChatNickMap = Record<number, string>;

const STORAGE_KEY = "chat_nicknames_v1";
const MAX_LEN = 40;

export function parseChatNicknames(raw: unknown): ChatNickMap {
  if (!raw || typeof raw !== "object") return {};
  const out: ChatNickMap = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const id = Number(k);
    if (!Number.isFinite(id) || id < 1) continue;
    const name = String(v ?? "").trim().slice(0, MAX_LEN);
    if (!name) continue;
    out[id] = name;
  }
  return out;
}

export function isChatNickMapEmpty(map: ChatNickMap): boolean {
  return Object.keys(map).length === 0;
}

export function readChatNicknames(userId: number): ChatNickMap {
  if (userId < 1) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return parseChatNicknames(parsed[String(userId)]);
  } catch {
    return {};
  }
}

export function writeChatNicknames(userId: number, map: ChatNickMap): void {
  if (userId < 1) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const store = parsed && typeof parsed === "object" ? parsed : {};
    store[String(userId)] = parseChatNicknames(map);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota / mode privé */
  }
}

export function setChatNickname(map: ChatNickMap, userId: number, name: string | null): ChatNickMap {
  const next = { ...map };
  const t = (name ?? "").trim().slice(0, MAX_LEN);
  if (!t) delete next[userId];
  else next[userId] = t;
  return next;
}

export function chatNickOrOfficial(userId: number, official: string, nicks: ChatNickMap): string {
  return nicks[userId]?.trim() || official;
}

export function applyNickToColleague(c: ChatColleague, nicks: ChatNickMap): ChatColleague {
  const n = nicks[c.id]?.trim();
  return n ? { ...c, display_name: n } : c;
}

export function applyNickToConversation(c: ChatConversation, nicks: ChatNickMap): ChatConversation {
  if (!c.members?.length) return c;
  let changed = false;
  const members = c.members.map((m) => {
    const n = nicks[m.id]?.trim();
    if (!n) return m;
    changed = true;
    return { ...m, display_name: n };
  });
  return changed ? { ...c, members } : c;
}
